import { Box, Stack, type Theme, useMediaQuery } from '@mui/material';
import type React from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIsValidatedConsultant } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getAskAnExpertEnabled } from '/@/store/ask-an-expert/selectors';
import { trackEvent } from '/@/store/nav/actions';
import { getSessionCountry, getUser } from '/@/store/session/selectors';
import { openWhatsappWeb } from '/@/utils';
import { reverse } from '/@/utils/router';
import CatalogAction from '/@/views/catalog/catalog-action';
import BottomBar, { BOTTOM_BAR_WIDTH } from '../app-navbar/bottombar';
import type { NavbarTabKey } from '../app-navbar/hooks';
import AppSidebar, { APP_SIDEBAR_WIDTH } from '../app-navbar/sidebar';
import styles from './styles.module.css';

export default function AppContent(props: {
    children?: React.ReactNode;
    hideAskAnExpert?: boolean;
    appSidebarTabKey?: NavbarTabKey;
    hideSidebar?: boolean;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const history = useNavigate();
    const isBr = useSelector((state) => getSessionCountry(state).code === 'BR');
    const dispatch = useDispatch();
    const enableAskAnExpert = useSelector((state) => getAskAnExpertEnabled(state));
    const offline = useSelector((state) => state.network.offline);
    const { children, hideAskAnExpert, appSidebarTabKey, hideSidebar = false } = props;
    const mounted = useRef(false);
    const user = useSelector(getUser);
    const showSidebar = user && !hideSidebar;

    useEffect(() => {
        const isIgnoredUrl = ['/sustainable-rotations-simulator-BR/', '/sustainable-rotations-simulator/'].some((url) =>
            location.pathname.includes(url)
        );
        if (!isIgnoredUrl && !mounted.current) {
            mounted.current = true;
            ref.current.scrollTo(0, 0);
        }
    }, []);

    useLayoutEffect(() => {
        ref.current?.scrollTo(0, 0);
    }, [location.pathname]);

    function handleClick() {
        dispatch(trackEvent({ key: 'askanexpert.open' }));
        if (isBr)
            openWhatsappWeb({
                phone: '+5511997535194',
            });
        const pathURL = window.location.pathname;
        const newSplitURL = pathURL.split('/', 4);
        const findURL = newSplitURL.some((item) => item === 'fields');
        let dataField = 'no-field';
        if (findURL && newSplitURL.slice(-1).pop() !== 'new' && newSplitURL.slice(-1).pop() !== '') {
            dataField = newSplitURL.slice(-1).pop();
        }
        return history(reverse('AskAnExpert', { params: { dataField } }));
    }

    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const isValidatedConsultant = useIsValidatedConsultant();

    return (
        // biome-ignore lint/correctness/useUniqueElementIds: Is used in PDF
        <Stack
            ref={ref}
            id='app-content'
            direction={isSmOrLower ? 'column' : 'row'}
            sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                overflowY: 'auto',
            }}
        >
            {showSidebar && !isSmOrLower && <AppSidebar selectedTab={appSidebarTabKey} />}
            <Box
                sx={[
                    {
                        width: '100%',
                    },
                    showSidebar && {
                        pl: isSmOrLower ? 0 : APP_SIDEBAR_WIDTH,
                        pb: isSmOrLower ? BOTTOM_BAR_WIDTH : 0,
                    },
                ]}
            >
                {children}
                {!offline && !hideAskAnExpert && enableAskAnExpert && !isSmOrLower && !isValidatedConsultant && (
                    <div className={styles.onDemandAskAnExpert}>
                        <CatalogAction
                            onClick={handleClick}
                            type={(isBr && 'chat-bot') || 'home'}
                            text={(isBr && _('Consulta a un experto através de CTBot')) || _('Consulta a un experto')}
                        />
                    </div>
                )}
            </Box>
            {showSidebar && isSmOrLower && <BottomBar selectedTab={appSidebarTabKey} />}
        </Stack>
    );
}
